/**
 * Parse persisted provision job log bodies from backend / job_service row processing.
 */

export interface ParsedProvisionLogMessage {
  spreadsheetRow?: number;
  contextFields: { key: string; value: string }[];
  /** Outcomes, validation errors, and diary lines (deduped row prefix when duplicated). */
  details: string[];
  /** Present when heuristic parsing yielded nothing usable — show verbatim. */
  rawFallback?: string;
}

/** Split a cramped single-line message into context + catalog error (`CSV spreadsheet row N: …`). */
function normalizeNewlines(raw: string): string {
  if (raw.includes("\n")) return raw;
  const m = raw.match(/\s+(?=CSV spreadsheet row \d+: )/iu);
  if (m?.index !== undefined && m.index > 0) {
    return `${raw.slice(0, m.index).trimEnd()}\n${raw.slice(m.index).trim()}`;
  }
  return raw;
}

/** True if substring before first "=" looks like a CSV context key (`store_id`, `email`). */
function looksLikeKvToken(beforeEq: string, afterEq: string): boolean {
  const key = beforeEq.trim();
  if (!key || /\s/.test(key)) return false;
  if (!/^[a-z][a-z0-9_]*$/i.test(key)) return false;
  return Boolean(afterEq.trim());
}

export function parseProvisionLogMessage(message: string | null | undefined): ParsedProvisionLogMessage {
  const raw = message?.trim() ?? "";
  if (!raw) {
    return { contextFields: [], details: [], rawFallback: "—" };
  }

  const normalized = normalizeNewlines(raw);
  const lines = normalized.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  let spreadsheetRow: number | undefined;
  const rowMatchEarly = normalized.match(/CSV spreadsheet row (\d+)/i);
  if (rowMatchEarly) {
    spreadsheetRow = parseInt(rowMatchEarly[1], 10);
  }

  const contextFields: { key: string; value: string }[] = [];
  const details: string[] = [];

  if (!lines.length) {
    return { spreadsheetRow, contextFields: [], details: [], rawFallback: raw };
  }

  const firstParts = lines[0]
    .split(" | ")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of firstParts) {
    if (/^CSV spreadsheet row \d+/i.test(part) && /\brow\s+1\s+is\s+the\s+header\b/i.test(part)) {
      continue;
    }
    const eq = part.indexOf("=");
    const before = eq >= 0 ? part.slice(0, eq) : "";
    const after = eq >= 0 ? part.slice(eq + 1) : "";
    if (eq > 0 && looksLikeKvToken(before, after)) {
      contextFields.push({
        key: before.trim().replace(/_/g, " "),
        value: after.trim()
      });
      continue;
    }
    if (!/^CSV spreadsheet row \d+/i.test(part)) {
      details.push(part);
    }
  }

  for (let i = 1; i < lines.length; i++) {
    let line = lines[i];
    const dup = line.match(/^CSV spreadsheet row \d+\s*:\s*(.+)$/iu);
    if (dup?.[1]) {
      line = dup[1].trim();
    }
    if (line) {
      details.push(line);
    }
  }

  const parsedNothing =
    spreadsheetRow === undefined && contextFields.length === 0 && details.length === 0;

  return {
    spreadsheetRow,
    contextFields,
    details,
    rawFallback: parsedNothing ? raw : undefined
  };
}

/**
 * Best-effort Workvivo SCIM user id from a persisted provision log `message` body.
 * Matches strings emitted by `job_service` (create / update / deactivate / lookup).
 */
export function extractWorkvivoUserIdFromLogMessage(message: string | null | undefined): string {
  const raw = message?.trim() ?? "";
  if (!raw) return "";

  const explicit = raw.match(/Workvivo user (?:created|updated|deactivated):\s*([^\s\r\n|]+)/i);
  if (explicit?.[1]) {
    return explicit[1].trim();
  }

  const foundWithId = raw.match(/User found in Workvivo with ID:\s*([^\s.|]+)/i);
  if (foundWithId?.[1]) {
    return foundWithId[1].trim();
  }

  return "";
}
